import TrashIcon from '@Resources/assets/icons/dark/trash.svg';
import { ITableEditOption, ITableEntity } from '@Utils/helpers/tableHelpers';
import { SvgIcon } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import _isNil from 'lodash/isNil';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Button from '@Components/Button/Button';
import CircularLoading from '@Components/CircularLoading/CircularLoading';
import DeleteDialog from '@Components/DeleteDialog/DeleteDialog';
import TextInput from '@Components/DomElements/TextInput';
import IconButton from '@Components/IconButton/IconButton';
import SettingsTable, {
    SettingsTableViewState,
} from '@Components/Settings/components/SettingsTable/SettingsTable';
import SettingsTableButton from '@Components/Settings/components/SettingsTable/SettingsTableButton';
import Styles from '@Components/Settings/views/SettingsViews.scss';
import OrganizationApi from '@Data/organizations/Api';
import {
    orgMembersNotInTeamState,
    selectedOrganizationState,
} from '@Data/organizations/Reducer';
import TeamsApi from '@Data/teams/Api';
import { selectedTeamState, teamMembersState } from '@Data/teams/Reducer';
import { IUpdateUser, IUser } from '@Data/users/Interfaces';
import { ISettingsBaseComponentProps } from '../../Interfaces';
import AddTeamMembersTable from './AddTeamMembersTable/AddTeamMembersTable';

interface IUserTableEntity extends IUser, ITableEntity {
    fullName: string;
}

const emptyUpdateUser = {
    userId: '',
    name: '',
    email: '',
    nickname: '',
};

const TeamMembersTable = (props: ISettingsBaseComponentProps) => {
    const { title, isSubmitting, setIsSubmitting, id } = props;
    const org = useSelector(selectedOrganizationState);
    const orgMembersNotInTeam = useSelector(orgMembersNotInTeamState);
    const team = useSelector(selectedTeamState);
    const teamMembers = useSelector(teamMembersState);
    const [selectedUser, setSelectedUser] = useState<IUser>(null);
    const [tableState, setTableState] = useState<SettingsTableViewState>(
        SettingsTableViewState.VIEW
    );
    const [updateUserEntity, setUpdateUserEntity] =
        useState<IUpdateUser>(emptyUpdateUser);
    const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
    const [deleteDialogIsOpen, setDeleteDialogIsOpen] =
        useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [addUserTableIsOpen, setAddMembersTableIsOpen] =
        useState<boolean>(false);

    useEffect(() => {
        getTeamMembers();
        getOrgMembers();
    }, []);

    const getTeamMembers = async () => {
        setIsLoading(true);
        await TeamsApi.getTeamMembers(team.team_id);
        setIsLoading(false);
    };

    const getOrgMembers = async () => {
        setIsLoading(true);
        await OrganizationApi.getOrganizationMembers(org.organisation_id);
        setIsLoading(false);
    };

    const columnHelper = createColumnHelper<IUserTableEntity | any>();
    const columns = [
        columnHelper.accessor('email', {
            cell: (info) => info.getValue(),
            header: 'Member IKE ID',
        }),
        columnHelper.accessor('nickname', {
            cell: (info) => {
                const user = info.row.original;
                return user.given_name && user.family_name
                    ? `${user.given_name} ${user.family_name}`
                    : '-';
            },
            header: 'Name',
        }),
        columnHelper.accessor('actions', {
            cell: null,
            header: 'Actions',
            enableSorting: false,
        }),
    ];

    const submitCleanup = async () => {
        setSelectedUser(null);
        setTableState(SettingsTableViewState.VIEW);
        setAddMembersTableIsOpen(false);
        setUpdateUserEntity(emptyUpdateUser);
        setIsSubmitting(false);
    };

    const handleAddUsers = async () => {
        setIsSubmitting(true);
        await TeamsApi.addMembersToTeam(team.team_id, selectedUserIds);
        setSelectedUserIds([]);
        submitCleanup();
        await getTeamMembers();
    };

    const handleUpdateUser = async () => {
        setIsSubmitting(true);
        await TeamsApi.deleteTeamMember(team.team_id, updateUserEntity.userId);
        submitCleanup();
        await getTeamMembers();
    };

    const handleSetEditState = () => {
        setUpdateUserEntity({
            userId: selectedUser.user_id,
            name: selectedUser.name,
            email: selectedUser.email,
            nickname: selectedUser.nickname,
        });
        setTableState(SettingsTableViewState.EDIT);
    };

    const editCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <TextInput disabled={true} /> },
        {
            attribute: 'nickname',
            component: <TextInput disabled={true} />,
            isStandalone: true,
        },
        {
            attribute: 'actions',
            isStandalone: true,
            component: (
                <IconButton
                    onClick={() => {
                        setUpdateUserEntity({
                            userId: selectedUser.user_id,
                            name: selectedUser.name,
                            email: selectedUser.email,
                            nickname: selectedUser.nickname,
                        });
                        setDeleteDialogIsOpen(true);
                    }}
                    style={{ backgroundColor: 'transparent' }}
                    icon={
                        <SvgIcon inheritViewBox>
                            <TrashIcon />
                        </SvgIcon>
                    }
                />
            ),
        },
    ];

    const createCells: Array<ITableEditOption> = [
        { attribute: 'email', component: <input type="text" /> },
        { attribute: 'nickname', component: null },
        { attribute: 'actions', component: null },
    ];

    const handleCancelUpdateUser = () => {
        setTableState(SettingsTableViewState.VIEW);
        setSelectedUser(null);
        setUpdateUserEntity(emptyUpdateUser);
        setAddMembersTableIsOpen(false);
        getTeamMembers();
    };

    const canCreateNewEntity =
        tableState === SettingsTableViewState.CREATE &&
        selectedUserIds.length > 0;

    return (
        <div className={Styles.settingsViewWrapper}>
            <div className={Styles.settingsViewHeader}>
                <span id={`${id}-title`}>{title}</span>
                <div>
                    {tableState !== SettingsTableViewState.VIEW && (
                        <Button
                            id={`cancel-edit-${id}-btn`}
                            color={'black'}
                            onClick={handleCancelUpdateUser}
                        >
                            Cancel
                        </Button>
                    )}
                    <SettingsTableButton
                        id={`edit-${id}-table-btn`}
                        tableState={tableState}
                        canEdit={!_isNil(selectedUser)}
                        createText={
                            selectedUserIds.length > 1
                                ? 'Add New Members'
                                : 'Add New Member'
                        }
                        editText={'Edit Member'}
                        setCreateState={() => {
                            setAddMembersTableIsOpen(true);
                            getOrgMembers();
                            setTableState(SettingsTableViewState.CREATE);
                        }}
                        setEditState={handleSetEditState}
                        onCreateNewEntity={handleAddUsers}
                        onEditEntity={handleUpdateUser}
                        canCreateNewEntity={canCreateNewEntity}
                        hideEditButton
                        isSubmitting={isSubmitting}
                    />
                </div>
            </div>
            <div className={Styles.tableContainer}>
                {isLoading ? (
                    <CircularLoading />
                ) : addUserTableIsOpen ? (
                    <AddTeamMembersTable
                        id="add-team-members-table"
                        users={orgMembersNotInTeam}
                        selectedUserIds={selectedUserIds}
                        setSelectedUserIds={setSelectedUserIds}
                    />
                ) : (
                    <SettingsTable
                        id={`${id}-table`}
                        columns={columns}
                        data={teamMembers}
                        onRowClick={(user) => setSelectedUser(user)}
                        selectedRow={selectedUser}
                        state={tableState}
                        updateEntity={updateUserEntity}
                        onChangeUpdateEntity={setUpdateUserEntity}
                        editCells={editCells}
                        createCells={createCells}
                        isSubmitting={
                            isSubmitting ||
                            tableState === SettingsTableViewState.EDIT
                        }
                    />
                )}
            </div>

            <DeleteDialog
                id={`${id}-delete-dialog`}
                isOpen={deleteDialogIsOpen}
                onClose={() => setDeleteDialogIsOpen(false)}
                onDelete={() => {
                    handleUpdateUser();
                    setDeleteDialogIsOpen(false);
                }}
                entityName={selectedUser?.email}
                isDeleting={isSubmitting}
            />
        </div>
    );
};

export default TeamMembersTable;
